import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { readResponse } from '../utils/checkout';
const API = (process.env.REACT_APP_BACKEND_URL || '');

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => { try { return localStorage.getItem('lpad_token'); } catch { return null; } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const activeToken = useRef(token);
  activeToken.current = token;

  const fetchMe = useCallback(async (t) => {
    if (!t) { setUser(null); setLoading(false); return; }
    setError(false);
    try {
      const res = await fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
      if (res.ok) { const data = await res.json(); if (activeToken.current === t) setUser(data); }
      else if (activeToken.current === t) {
        if (res.status === 401 || res.status === 403) {
          try { localStorage.removeItem('lpad_token'); } catch { }
          setToken(null); setUser(null);
        } else setError(true);
      }
    } catch { if (activeToken.current === t) setError(true); }
    if (activeToken.current === t) setLoading(false);
  }, []);

  useEffect(() => { fetchMe(token); }, [token, fetchMe]);

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API}/api/auth/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) });
    const data = await readResponse(res, 'Erreur de connexion');
    if (!data.token || !data.user) throw new Error('Erreur de connexion');
    try { localStorage.setItem('lpad_token', data.token); } catch { }
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (name, email, password, locale = 'fr') => {
    const res = await fetch(`${API}/api/auth/register`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, email, password, locale }) });
    const data = await readResponse(res, 'Erreur d’inscription');
    if (!data.token || !data.user) throw new Error('Erreur d’inscription');
    try { localStorage.setItem('lpad_token', data.token); } catch { }
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    try { localStorage.removeItem('lpad_token'); } catch { }
    activeToken.current = null;
    setToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(() => fetchMe(token), [token, fetchMe]);

  return (
    <AuthContext.Provider value={{ user, token, loading, error, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
